<template>
  <v-container id="moduleSurveyExplorer">
    <v-card>
      <v-card-title class="mb-0">
        {{ $t('moduleSurveyExplorer.title') }}
        <v-spacer />
      </v-card-title>
      <v-progress-linear
        :aria-label="$t('moduleSurveyExplorer.loading')"
        :active="busy"
        indeterminate
      />
      <v-card-text class="mt-5">
        {{ $t('moduleSurveyExplorer.description') }}
      </v-card-text>
      <v-card-text class="mt-5">
        <v-row>
          <v-col><v-select v-model="selectedModule" :items="getModuleNames(modules)" :label="$t('globals.module')" dense /></v-col>
          <v-col><v-select v-model="selectedDevice" :items="devices" :label="$t('moduleSurveyExplorer.device')" dense /></v-col>
        </v-row>
      </v-card-text>
      <div id="success-factor-content">
        <v-row v-if="roleSysAdmin">
          <v-col>
            <div class="NPS">
              NPS {{ npsScoreCalculated }}
            </div>
          </v-col>
        </v-row>
        <v-card-text v-if="selectedModule">
          <h3>{{ $t('moduleSurveyExplorer.feedback-survey') }}</h3>
          <p v-html="$t('moduleSurveyExplorer.feedback-survey-description')" />
          <v-row v-if="(postSurveys.length > 0)" id="module-survey-explorer-pie-chart">
            <v-col v-for="item in postSurveys" :key="item.id">
              <h3>{{ headerText(item) }}</h3>

              <google-chart
                type="PieChart"
                :data="item.data"
                :options="item.chartOptions"
              />
              <span class="question-text">"{{ questionText(item) }}"</span>
            </v-col>
          </v-row>
          <v-row v-if="(postSurveys.length==0)">
            <v-col><i>{{ $t('moduleSurveyExplorer.insufficent-data-available') }}</i></v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-if="selectedModule && selectedModule !==ALL_MODULES ">
          <h3>{{ $t('moduleSurveyExplorer.self-assessment-survey') }}</h3>
          <p v-html="$t('moduleSurveyExplorer.self-assessment-survey-description')" />
          <v-row>
            <v-col v-for="item in selfAssessmentSurveys" :key="item.id">
              <google-chart

                type="ColumnChart"
                :data="item.data"
                :options="item.chartOptions"
              />
              <span class="question">"{{ questionText(item) }}"</span>
            </v-col>
          </v-row>
          <v-row v-if="selfAssessmentSurveys.length===0 && selectedModule !==ALL_MODULES">
            <v-col><i>{{ $t('moduleSurveyExplorer.insufficent-data-available') }}</i></v-col>
          </v-row>
        </v-card-text>
      </div>
    </v-card>
  </v-container>
</template>


<script>
  import api from '@/helpers/api.js'
  import { GChart } from 'vue-google-charts'
  import { Roles } from '@/helpers/users.js'
  import { ALL_MODULES } from '@/helpers/accounts.js'
  import { reportScopes } from '@/helpers/reportScopes.js'
  import { ContentLibrary } from '@/helpers/contentLibrary.js'


  export default {
    name: 'ModuleSurveyExplorer',

    components: {
      'google-chart': GChart,
    },
    props: {
      startDate: { type: Date, required: true, default: null },
      endDate: { type: Date, required: true, default: null },
      classId: { type: String, required: false, default: null },
      scope: { type: String, required: true, default: 'account' },
    },

    data: () => ({
      busy: false,
      data: undefined,
      dates: undefined,
      allData: undefined,
      npsData: [],
      chartOptions: {
        title: 'title',
        width: 250,
        height: 250,
        isStacked: false,
        legend: 'none', //
        fontName: 'Inter',
        colors: ['#658da6', '#ff9416', '#3f3b73', '#ffa758', '#192e40'],
        pieHole: 0.4,
        pieSliceTextStyle: {
          color: 'black',
        },
        vAxis: {
          minValue: 0,
          maxValue: 1,
          format: '#%',
        },
        chartArea: { width: '100%' },
      },
      selectedModule: undefined,
      selectedDevice: undefined,
      devices: ['All'],
      testData: [
      ],
    }),
    computed: {
      period () {
        return {
          start: this.startDate,
          end: this.endDate,
        }
      },
      npsScoreCalculated () {
        let score = { promoter: 0, detractor: 0, passive: 0 }
        const noResultString = this.$t('moduleSurveyExplorer.unavailable')
        if (this.selectedModule === ALL_MODULES) {
          if (this.allData === undefined) {
            return noResultString
          }
          const keys = Object.keys(this.allData)
          keys.forEach((x) => {
            const nps = this.allData[x].postSurvey?.recommendationNps
            if (nps !== undefined) {
              score.promoter += nps.promoter
              score.passive += nps.passive
              score.detractor += nps.detractor
              // console.log(`${x} NPS is ${Math.round(((nps.promoter - nps.detractor) / (nps.promoter + nps.detractor + nps.passive)) * 100)} => ${JSON.stringify(nps)}`)
            }
          })
        } else {
          if (this.data === undefined) {
            return noResultString
          }
          score = this.data[this.selectedModule]?.postSurvey?.recommendationNps
        }
        if (score === undefined) {
          return noResultString
        }
        const totalScore = score.promoter + score.detractor + score.passive
        if (totalScore === 0) {
          return noResultString
        }
        const finalNps = Math.round(((score.promoter - score.detractor) / totalScore) * 100)
        // console.log(`Final NPS is ${finalNps} => ${JSON.stringify(score)}`)
        return finalNps
      },
      modules () {
        if (!this.data) return []
        return [...Object.keys(this.data), ALL_MODULES].sort()
      },
      /* modulesWithNames () {
        const map = this.modules().map((x) => { return {
          name: this.$store.getters.moduleName(x),
          id: x,
        }
        })
        console.log(map)
        return map
      }, */
      roleSysAdmin () {
        return (this.$store.getters.currentUserProfile.user.roles.includes('sysadmin'))
      },
      selfAssessmentSurveys () {
        if (this.selectedModule === ALL_MODULES) return []
        if (!this.data || !this.selectedModule) return []
        if (!this.data[this.selectedModule]) return []
        if (!this.data[this.selectedModule].selfAssessment) return []
        const data = this.data[this.selectedModule].selfAssessment
        var header = ['before', 'after', { role: 'style' }, { role: 'annotation' }]
        const result = Object.entries(data).map(([key, value]) => { return { id: key, module: this.selectedModule, data: this.convertToBeforeAfterDataTable(header, value), chartOptions: { ...this.chartOptions, title: key } } })
        // iterate throw if data is empty then remove from array
        return result
      },
      postSurveys () {
        let data = {}
        let postSurvey = {}
        if (!this.data || !this.selectedModule) return []
        if (this.selectedModule === ALL_MODULES) {
          postSurvey = this.getAllModulesData()
          const { recommendationNps, ...feedbackSurvey } = postSurvey
          data = feedbackSurvey
        }
        if (this.data[this.selectedModule] && !this.data[this.selectedModule].postSurvey) return []
        if (this.data && this.data[this.selectedModule] && this.selectedModule !== ALL_MODULES) {
          postSurvey = this.data[this.selectedModule].postSurvey
          const { recommendationNps, ...feedbackSurvey } = postSurvey
          data = feedbackSurvey
        }

        if ('confidence' in data) delete data.confidence
        if ('effectiveness' in data) delete data.effectiveness
        const header = ['yes', 'no', { role: 'style' }, { role: 'annotation' }]
        const result = Object.entries(data).map(([key, value]) => {
          return { id: key, module: 'postSurvey', data: this.convertToPostSurveyDataTable(header, value), chartOptions: { ...this.chartOptions, title: '', colors: ['#ff9416', '#658da6'] } } })
        return result
      },
      account () {
        if (this.$route.query.account) {
          return this.$route.query.account
        } else {
          return this.$store.getters.currentUserProfile.user.account
        }
      },
    },

    watch: {
      async period () {
        await this.datesChanged()
      },
      selectedDevice: async function (newValue, oldValue) {
        this.busy = true
        const deviceType = newValue
        if (deviceType === 'All') {
          await this.refreshItems()
          this.data = this.allData
          this.busy = false
        } else {
          const role = this.scope === reportScopes.GLOBAL ? Roles.SYSADMIN : undefined
          const account = this.scope !== reportScopes.GLOBAL ? this.account : undefined
          const classId = this.scope === reportScopes.CLASS ? this.classId : undefined
          const { results } = await api.getSurveyReport(this.startDate, this.endDate, { role: role, classid: classId, account: account, devicetype: deviceType })
          this.busy = false
          this.data = results
          if (this.data[this.selectedModule] && this.data[this.selectedModule].postSurvey) {
            const postSurveyData = this.data[this.selectedModule].postSurvey
            const { recommendationNps } = postSurveyData
            this.npsData = [recommendationNps]
          } else {
            this.npsData = []
          }
        }
      },
      selectedModule: function (newVal, oldVal) {
        if (this.data[newVal]) {
          const data = this.data[newVal].postSurvey
          if (data) {
            const { recommendationNps } = data
            this.npsData = [recommendationNps]
          } else {
            this.npsData = []
          }
        }
      },
      // Watch for ContentLibrary changes and update module names when it becomes available
      '$store.state.contentLibrary': {
        handler (newContentLibrary) {
          if (newContentLibrary) {
            // Force reactivity update for computed properties that depend on ContentLibrary
            this.$forceUpdate()
          }
        },
        immediate: false,
      },
    },
    created () {
      this.ALL_MODULES = ALL_MODULES
    },
    async mounted () {
      try {
        // Ensure ContentLibrary is loaded first
        await ContentLibrary.Get(this.$store, this.$i18n.locale)
        await this.refreshItems()
      } catch (error) {
        console.error('Error loading ModuleSurveyExplorer component:', error)
        // Still try to load survey data even if ContentLibrary fails
        await this.refreshItems()
      }
    },


    methods: {
      keyText (option) {
        return this.modules.map(x => { return { text: this.$t('modules.' + x), value: x } })
      },
      getModuleNames (option) {
        return this.modules.map(x => {
          if (x === ALL_MODULES) {
            return { text: this.$t('modules.' + x), value: x }
          } else {
            try {
              return { text: ContentLibrary.NameOf(this.$store, this.$i18n.locale, x), value: x }
            } catch (error) {
              console.warn('Error translating module name:', x, error)
              return { text: x, value: x }
            }
          }
        })
      },
      convertToBeforeAfterDataTable (header, values) {
        const output = Object.assign([], [header])
        if (values.length > 1 && values[0]) {
          const beforePassPercentage = Math.ceil((values[0].pass / (values[0].pass + values[0].fail)) * 100)
          const afterPassPercentage = Math.ceil((values[1].pass / (values[1].pass + values[1].fail)) * 100)
          
          output.push([header[0], beforePassPercentage, this.chartOptions.colors[0], `${beforePassPercentage}%`])
          output.push([header[1], afterPassPercentage, this.chartOptions.colors[1], `${afterPassPercentage}%`])
          return output
        } else {
          return null
        }
      },
      getAllModulesData () {
        const modules = Object.keys(this.data)
        let data = {}
        let recommendationNpsData = { pass: 0, fail: 0 }

        modules.forEach((module) => {
          if (this.data[module]) {
            for (const i in this.data[module].postSurvey) {
              if (i !== 'recommendationNps') {
                if (i in data) {
                  if (this.data[module].postSurvey[i][0] && data[i][0]) {
                    const { fail: newFailValue, pass: newPassValue } = this.data[module].postSurvey[i][0]
                    const { fail: oldFailValue, pass: oldPassValue } = data[i][0]
                    const newPassCount = oldPassValue + newPassValue
                    const newFailCount = oldFailValue + newFailValue
                    const newValue = [{ pass: newPassCount, fail: newFailCount }]
                    data = { ...data, ...{ [i]: newValue } }
                  }
                } else {
                  data = { ...data, ...{ [i]: this.data[module].postSurvey[i] } }
                }
              } else if (i === 'recommendationNps') {
                const { pass: oldPassCount, fail: oldFailCount } = recommendationNpsData
                const { pass: newPassCount, fail: newFailCount } = this.data[module].postSurvey[i]
                recommendationNpsData = { pass: oldPassCount + newPassCount, fail: oldFailCount + newFailCount }
              }
            }
          }
        })
        this.npsData = [recommendationNpsData]
        return data
      },
      localise (key) {
        this.$t('')
        return this.$t('moduleSurveyExplorer.survey-questions.' + key)
      },
      questionText (item) {
        if (item) {
          return this.localise(item.module + '/' + item.id)
        }
      },
      headerText (item) {
        return this.localise(item.module + '/' + item.id + '/title')
      },
      convertToPostSurveyDataTable (header, values) {
        const output = Object.assign([], [header])
        if (values.length > 0) {
          const { pass, fail } = values[0]
          output.push([header[0], pass, this.chartOptions.colors[0], pass])
          output.push([header[1], fail, this.chartOptions.colors[1], fail])
          // console.log({ oe: output })
          return output
        }
      },
      createGoogleChartOptions (header) {
        const res = { ...this.chartOptions, title: header }
        return res
      },
      datesChanged () {
        this.selectedModule = undefined
        this.refreshItems()
      },
      async refreshItems () {
        this.busy = true
        let rawResults
        console.log('Survey data: fetching')
        switch (this.scope) {
          case reportScopes.GLOBAL:
            rawResults = await api.getSurveyReport(this.startDate, this.endDate, { role: Roles.SYSADMIN }) || {}
            break
          case reportScopes.CLASS:
            rawResults = await api.getClassSurveyReport(this.startDate, this.endDate, this.account, this.classId) || {}
            break
          default:
            rawResults = await api.getSurveyReport(this.startDate, this.endDate, { account: this.account }) || {}
            break
        }
        console.log('Survey data: downloaded')
        // console.dir(rawResults)
        const { results, deviceLists } = rawResults
        this.data = this.allData = results || {}
        this.devices = deviceLists ? [...this.devices, ...deviceLists] : this.devices

        // console.log('data', this.data)

        const keys = Object.keys(this.data)
        if (keys.length > 0) {
          this.selectedModule = keys[0]
        }
        this.busy = false
      },
    },
  }

</script>

<style scoped>
.question {
  font-size: 14px;
  font-weight: bold;
}

input {
  color: white !important;
}

.NPS {
  margin: 15px;
  text-align: center;
  background-color: #65bd60;
  color: rgb(0, 0, 0);
  font-size: 18px;
  padding-top: 10px;
  padding-bottom: 10px;
}


</style>
